import { useEffect, useRef, useState } from 'react'
import { Html } from '@react-three/drei'
import { SUBTYPE_OPTIONS } from '../constants/furniture'

// 数値入力コンポーネント
function EditableValue({ value, onChange, min, max, suffix = '' }) {
    const [isEditing, setIsEditing] = useState(false)
    const [inputValue, setInputValue] = useState(value.toString())
    const inputRef = useRef(null)

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    useEffect(() => {
        setInputValue(value.toString())
    }, [value])

    const handleSubmit = () => {
        const numValue = parseInt(inputValue, 10)
        if (!isNaN(numValue)) {
            const clampedValue = Math.max(min, Math.min(max, numValue))
            onChange(clampedValue)
        }
        setIsEditing(false)
    }

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                type="number"
                min={min}
                max={max}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onBlur={handleSubmit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmit()
                    if (e.key === 'Escape') setIsEditing(false)
                }}
                className="w-12 text-xs text-white text-right bg-gray-700 border border-white/30 rounded px-1 py-0.5 focus:outline-none focus:border-primary-400"
            />
        )
    }

    return (
        <span
            onClick={() => setIsEditing(true)}
            className="text-xs text-white w-12 text-right cursor-pointer hover:bg-white/10 px-1 py-0.5 rounded"
            title="クリックで編集"
        >
            {value}{suffix}
        </span>
    )
}

export default function ContextMenu({
    furniture,
    position3D,
    onClose,
    onUpdateSize,
    onUpdateRotation,
    onUpdateSubtype,
    onDelete,
    onDuplicate,
    collisionResult,
    furnitureList,
    ignoredCollisions,
    onIgnoreCollision,
    onUnignoreCollision,
}) {
    // 干渉情報を取得
    const getCollisionInfo = () => {
        if (!collisionResult || !furniture) return { collisions: [], isOutOfBounds: false }

        const collisions = []
        const isOutOfBounds = collisionResult.outOfBounds?.has(furniture.id) || false

        if (collisionResult.collisions?.has(furniture.id)) {
            const collidingIds = collisionResult.collisions.get(furniture.id)
            collidingIds.forEach(otherId => {
                const key = [furniture.id, otherId].sort().join(':')
                const isIgnored = ignoredCollisions?.has(key) || false
                const otherFurniture = furnitureList?.find(f => f.id === otherId)
                if (otherFurniture) {
                    collisions.push({
                        id: otherId,
                        name: otherFurniture.name,
                        icon: otherFurniture.icon,
                        isIgnored
                    })
                }
            })
        }

        return { collisions, isOutOfBounds }
    }

    const { collisions, isOutOfBounds } = getCollisionInfo()
    const menuRef = useRef(null)
    const [menuOffset, setMenuOffset] = useState({ x: 140, y: 0 })
    const [isDragging, setIsDragging] = useState(false)
    const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 })

    // ESCキーでメニューを閉じる
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [onClose])

    // メニュー外クリックで閉じる
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                onClose()
            }
        }
        const timer = setTimeout(() => {
            window.addEventListener('pointerdown', handleClickOutside)
        }, 100)
        return () => {
            clearTimeout(timer)
            window.removeEventListener('pointerdown', handleClickOutside)
        }
    }, [onClose])

    // ドラッグ処理
    const handleDragStart = (e) => {
        e.stopPropagation()
        setIsDragging(true)
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            offsetX: menuOffset.x,
            offsetY: menuOffset.y
        }
    }

    useEffect(() => {
        if (!isDragging) return

        const handleDragMove = (e) => {
            const dx = e.clientX - dragStartRef.current.x
            const dy = e.clientY - dragStartRef.current.y
            setMenuOffset({
                x: dragStartRef.current.offsetX + dx,
                y: dragStartRef.current.offsetY + dy
            })
        }

        const handleDragEnd = () => {
            setIsDragging(false)
        }

        window.addEventListener('pointermove', handleDragMove)
        window.addEventListener('pointerup', handleDragEnd)

        return () => {
            window.removeEventListener('pointermove', handleDragMove)
            window.removeEventListener('pointerup', handleDragEnd)
        }
    }, [isDragging])

    if (!furniture) return null

    const subtypeOptions = SUBTYPE_OPTIONS[furniture.category] || SUBTYPE_OPTIONS.custom

    return (
        <Html
            position={position3D}
            style={{
                pointerEvents: 'auto',
                transform: `translate(${menuOffset.x}px, ${menuOffset.y}px)`,
            }}
        >
            <div
                ref={menuRef}
                className="context-menu glass-effect rounded-xl p-3 min-w-[200px] max-w-[240px] shadow-2xl border border-white/20"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
            >
                {/* ヘッダー（ドラッグ可能） */}
                <div
                    className="flex items-center justify-between mb-2 pb-2 border-b border-white/10 cursor-move"
                    onPointerDown={handleDragStart}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-lg">{furniture.icon}</span>
                        <span className="text-sm font-medium text-white">{furniture.name}</span>
                    </div>
                    <button
                        onClick={onClose}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="text-gray-400 hover:text-white transition-colors text-sm"
                    >
                        ✕
                    </button>
                </div>

                {/* タイプ選択 */}
                <div className="mb-2">
                    <label className="text-xs text-gray-400 block mb-1">🎨 タイプ</label>
                    <select
                        value={furniture.subtype || 'default'}
                        onChange={(e) => onUpdateSubtype(furniture.id, e.target.value)}
                        className="w-full px-2 py-1 text-sm bg-gray-800 border border-white/20 rounded text-white focus:outline-none focus:border-primary-400"
                    >
                        {subtypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value} className="bg-gray-800 text-white">
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* サイズ＆回転バー（統一レイアウト） */}
                {onUpdateSize && (
                    <div className="mb-2 space-y-1.5">
                        <label className="text-xs text-gray-400 block">📏 サイズ・回転</label>

                        {/* 幅 */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-blue-400 w-6">横</span>
                            <input
                                type="range"
                                min="20"
                                max="300"
                                value={furniture.size.width}
                                onChange={(e) => onUpdateSize(furniture.id, 'width', Number(e.target.value))}
                                className="flex-1 h-1 bg-white/10 rounded appearance-none cursor-pointer accent-blue-500"
                            />
                            <EditableValue
                                value={furniture.size.width}
                                onChange={(val) => onUpdateSize(furniture.id, 'width', val)}
                                min={20}
                                max={300}
                            />
                        </div>

                        {/* 奥行 */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-blue-400 w-6">縦</span>
                            <input
                                type="range"
                                min="20"
                                max="300"
                                value={furniture.size.depth}
                                onChange={(e) => onUpdateSize(furniture.id, 'depth', Number(e.target.value))}
                                className="flex-1 h-1 bg-white/10 rounded appearance-none cursor-pointer accent-blue-500"
                            />
                            <EditableValue
                                value={furniture.size.depth}
                                onChange={(val) => onUpdateSize(furniture.id, 'depth', val)}
                                min={20}
                                max={300}
                            />
                        </div>

                        {/* 高さ */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-purple-400 w-6">高</span>
                            <input
                                type="range"
                                min="20"
                                max="300"
                                value={furniture.size.height}
                                onChange={(e) => onUpdateSize(furniture.id, 'height', Number(e.target.value))}
                                className="flex-1 h-1 bg-white/10 rounded appearance-none cursor-pointer accent-purple-500"
                            />
                            <EditableValue
                                value={furniture.size.height}
                                onChange={(val) => onUpdateSize(furniture.id, 'height', val)}
                                min={20}
                                max={300}
                            />
                        </div>

                        {/* 回転 */}
                        {onUpdateRotation && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-green-400 w-6">↻</span>
                                <input
                                    type="range"
                                    min="-180"
                                    max="180"
                                    step="5"
                                    value={furniture.rotation || 0}
                                    onChange={(e) => onUpdateRotation(furniture.id, Number(e.target.value))}
                                    className="flex-1 h-1 bg-white/10 rounded appearance-none cursor-pointer accent-green-500"
                                />
                                <EditableValue
                                    value={furniture.rotation || 0}
                                    onChange={(val) => onUpdateRotation(furniture.id, val)}
                                    min={-180}
                                    max={180}
                                    suffix="°"
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* 干渉情報 */}
                {(collisions.length > 0 || isOutOfBounds) && (
                    <div className="mb-2 p-2 bg-red-500/10 border border-red-500/30 rounded">
                        <label className="text-xs text-red-400 block mb-1">⚠️ 干渉</label>
                        {isOutOfBounds && (
                            <div className="text-xs text-red-300 mb-1">
                                📦 部屋の境界外
                            </div>
                        )}
                        {collisions.map((c) => (
                            <div
                                key={c.id}
                                className={`text-xs ${c.isIgnored ? 'text-yellow-400' : 'text-red-300'} flex items-center justify-between gap-1 py-0.5`}
                            >
                                <div className="flex items-center gap-1">
                                    <span>{c.icon}</span>
                                    <span>{c.name}</span>
                                </div>
                                {c.isIgnored ? (
                                    <button
                                        onClick={() => onUnignoreCollision?.(furniture.id, c.id)}
                                        className="px-1.5 py-0.5 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded text-[10px]"
                                    >
                                        解除
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => onIgnoreCollision?.(furniture.id, c.id)}
                                        className="px-1.5 py-0.5 bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 rounded text-[10px]"
                                    >
                                        無視
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* 削除・複製ボタン */}
                <div className="pt-2 border-t border-white/10 flex gap-2">
                    {onDuplicate && (
                        <button
                            onClick={() => {
                                onDuplicate(furniture.id)
                                onClose()
                            }}
                            className="flex-1 py-1.5 px-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 text-xs rounded transition-colors flex items-center justify-center gap-1"
                        >
                            📋 複製
                        </button>
                    )}
                    <button
                        onClick={() => {
                            onDelete(furniture.id)
                            onClose()
                        }}
                        className="flex-1 py-1.5 px-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs rounded transition-colors flex items-center justify-center gap-1"
                    >
                        🗑️ 削除
                    </button>
                </div>
            </div>
        </Html>
    )
}
